import { FC } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { BlobProvider } from '@react-pdf/renderer';

import {
  PencilAltIcon,
  ExternalLinkIcon,
  TrashIcon,
} from '@heroicons/react/outline';

import { ProviderActivityType } from '../../types/activity';

import {
  trackProviderEvent,
  AnalyticsPage,
  AnalyticsComponent,
  CommonAnalyticsEvent,
} from '../../../lib/analytics';

import {
  getAssessmentQuestionType,
  isAssessmentAForm,
} from '../../lib/assessments';

import { getTimeStamp } from '../../lib/time';
import { getProviderActivityData } from '../../lib/providerActivity';
import { useAuth } from '../../../contexts/AuthContext';

import Button from '../../components/Button';
import LibraryFormPDF from './LibraryFormPDF';
import PrintablePdfDownloadButton from './PrintablePdfDownloadButton';
import Slideover from '../../components/Slideover';
import AudioPlayer from '../../components/AudioPlayer';
import SlideoverSection from '../../components/SlideoverSection';
import QuestionsSection from '../../components/Assessments/QuestionsSection';
import InterpretationSection from '../../components/Assessments/InterpretationSection';
import ResponseOptionsSection from '../../components/Assessments/ResponseOptionsSection';
import {
  ActivityListDataFragment,
  ActivityType,
  AssessmentQuestionType,
  useActivityQuery,
} from '../../../generated/graphql';

const ProviderActivitySlideover: FC<{
  isOpen: boolean;
  onClose: () => void;
  followUpMode?: boolean;
  selectedProviderActivity: ActivityListDataFragment | undefined;
  setIsConfirmArchiveActivityModalOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectedProviderCollection?: { id: string } | undefined;
}> = ({
  isOpen,
  onClose,
  followUpMode = false,
  selectedProviderActivity,
  setIsConfirmArchiveActivityModalOpen,
  selectedProviderCollection,
}) => {
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();
  const wordmarkLogoUrl = authedProviderUser?.wordmarkLogoMedia?.url;

  const hasArchivalFunctionality = Boolean(
    setIsConfirmArchiveActivityModalOpen,
  );

  const isActivityAudio =
    selectedProviderActivity?.__typename === ProviderActivityType.Audio;

  const isActivityImage =
    selectedProviderActivity?.__typename === ProviderActivityType.Image;

  const isActivityLink =
    selectedProviderActivity?.__typename === ProviderActivityType.Link;

  const isActivityPDF =
    selectedProviderActivity?.__typename === ProviderActivityType.PDF;

  const isActivityAssessment =
    selectedProviderActivity?.__typename === ProviderActivityType.Assessment;

  const isActivityForm =
    isActivityAssessment &&
    isAssessmentAForm(selectedProviderActivity.assessment);

  // The library list query no longer fetches the full assessment form definition
  // (questions, interpretation, instructions) to keep the list payload small
  // (Sentry WEB-63). Those heavy fields are only needed when previewing a single
  // assessment here, so we fetch the full activity on demand when the slideover
  // is opened for an assessment.
  const { data: detailedActivityData, loading: loadingDetailedActivity } =
    useActivityQuery({
      variables: {
        activityId: selectedProviderActivity?.id ?? '',
        activityType: ActivityType.Assessment,
      },
      skip: !isOpen || !isActivityAssessment || !selectedProviderActivity?.id,
    });

  // Fully-detailed assessment (with questions/interpretation/instructions),
  // available once the on-demand fetch resolves.
  const detailedAssessment =
    detailedActivityData?.activity?.__typename ===
    ProviderActivityType.Assessment
      ? detailedActivityData.activity.assessment
      : undefined;

  const providerActivityData =
    selectedProviderActivity &&
    getProviderActivityData(selectedProviderActivity);

  const ActivityThumbnail = providerActivityData?.ActivityThumbnail;
  const activityImage = providerActivityData?.activityImage;

  const isImageAudioLinkOrPDF =
    isActivityImage || isActivityAudio || isActivityLink || isActivityPDF;

  const printableFormAssessment =
    isActivityForm && detailedAssessment ? detailedAssessment : null;

  const hasPrintableFormQuestions = Boolean(
    printableFormAssessment?.questions?.length,
  );

  const arePrintableFormQuestionsReady =
    hasPrintableFormQuestions &&
    printableFormAssessment!.questions.every((question) => {
      const questionType = getAssessmentQuestionType(question);
      return (
        questionType != null &&
        questionType !== AssessmentQuestionType.Unsupported
      );
    });

  const printableFormQuestionKey =
    printableFormAssessment?.questions
      ?.map(
        (question) =>
          `${question.__typename ?? 'unknown'}:${
            getAssessmentQuestionType(question) ?? 'none'
          }`,
      )
      .join('|') ?? '';

  const printablePdfDocument = printableFormAssessment ? (
    <LibraryFormPDF
      assessment={printableFormAssessment}
      wordmarkLogoUrl={wordmarkLogoUrl}
    />
  ) : null;

  const TitleComponent = (
    <>
      {isActivityForm && selectedProviderActivity && (
        <div className="mt-3 flex flex-col items-start gap-3">
          <div className="flex flex-row flex-wrap items-center gap-3">
            {!followUpMode && (
              <Button
                title="Edit form"
                theme="secondary"
                size="small"
                IconComponent={PencilAltIcon}
                onClick={() => {
                  const assessmentId = selectedProviderActivity?.assessment.id;
                  if (assessmentId) {
                    if (selectedProviderCollection?.id) {
                      navigate(
                        `/library/forms/${assessmentId}?collection=${selectedProviderCollection.id}`,
                      );
                    } else {
                      navigate(`/library/forms/${assessmentId}`, {
                        state: {
                          fromPage: 'library',
                        },
                      });
                    }
                  }
                }}
              />
            )}
            {hasArchivalFunctionality && (
              <Button
                title="Delete form"
                iconClassName="text-red-100"
                theme="destructive"
                IconComponent={TrashIcon}
                size="small"
                onClick={() => {
                  setIsConfirmArchiveActivityModalOpen?.(true);
                  trackProviderEvent(CommonAnalyticsEvent.ButtonClicked, {
                    buttonName: 'Delete resource',
                    originPage: AnalyticsPage.Library,
                    originComponent: AnalyticsComponent.ActivitySlideover,
                  });
                }}
              />
            )}
          </div>
          {arePrintableFormQuestionsReady && !loadingDetailedActivity && (
            <BlobProvider
              key={`${printableFormAssessment?.id}:${printableFormQuestionKey}`}
              document={printablePdfDocument}
            >
              {({ blob, loading, error }) => (
                <PrintablePdfDownloadButton
                  blob={blob}
                  loading={loading}
                  error={error}
                  formName={selectedProviderActivity.assessment.name}
                  assessmentId={selectedProviderActivity.assessment.id}
                />
              )}
            </BlobProvider>
          )}
        </div>
      )}
      {isImageAudioLinkOrPDF && (
        <div className="flex w-full flex-col items-start justify-start">
          <div className="flex flex-row items-center justify-start">
            {Boolean(activityImage) && (
              <img
                src={activityImage}
                className="h-[88px] w-[88px] rounded-lg object-cover"
                alt="Activity thumbnail"
              />
            )}
            {!activityImage && ActivityThumbnail && (
              <ActivityThumbnail className="min-h-[88px] min-w-[88px] rounded-lg" />
            )}
            <div className="ml-4 flex flex-col items-start justify-center">
              <div className="text-small-caption text-neutral-125/75">
                Added {getTimeStamp(providerActivityData?.createdAt, false)}
              </div>
              <div className="my-1 text-left text-category font-medium text-green-150">
                {providerActivityData?.activityTitle}
              </div>
              {providerActivityData?.activitySubtitle && (
                <div className="text-caption text-neutral-125/75">
                  {providerActivityData?.activitySubtitle}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <Slideover
      isAboveModal={followUpMode}
      isOpen={isOpen}
      onClose={onClose}
      {...(!isImageAudioLinkOrPDF && {
        title: providerActivityData?.activityTitle,
        subtitle: providerActivityData?.activitySubtitle,
      })}
      {...((isImageAudioLinkOrPDF || isActivityForm) && {
        titleComponent: TitleComponent,
      })}
    >
      {isImageAudioLinkOrPDF && (
        <div
          className={classNames(
            'flex w-full items-start justify-start',
            isActivityAudio ? 'flex-col' : 'flex-row',
          )}
        >
          {isActivityAudio && (
            <div className="w-full rounded-md bg-white p-4">
              <AudioPlayer
                playerId={
                  providerActivityData?.activityId ?? 'provider-activity-player'
                }
                mediaUrl={providerActivityData?.activityMediaUrl}
                duration={providerActivityData?.activityDuration}
                triggerStop={!isOpen}
              />
            </div>
          )}
          <div className="flex w-full flex-row items-center justify-start">
            {!isActivityAudio && (
              <Button
                title="Preview resource"
                theme="secondary-white"
                size="small"
                IconComponent={ExternalLinkIcon}
                onClick={(e) => {
                  window.open(providerActivityData?.activityMediaUrl, '_blank');
                  e.preventDefault();
                }}
              />
            )}
            {hasArchivalFunctionality && (
              <Button
                title="Delete"
                theme="secondary-white"
                size="small"
                iconClassName="text-red-100"
                IconComponent={TrashIcon}
                onClick={() => setIsConfirmArchiveActivityModalOpen?.(true)}
                className={classNames(
                  'text-red-100',
                  !isActivityAudio ? 'ml-4' : 'mt-6',
                )}
              />
            )}
          </div>
        </div>
      )}

      {isActivityAssessment && (
        <>
          {detailedAssessment?.instructions && (
            <SlideoverSection
              title="Instructions"
              textContent={detailedAssessment.instructions}
            />
          )}
          {isActivityForm ? (
            <SlideoverSection
              title="Description"
              textContent={
                detailedAssessment?.description ??
                (selectedProviderActivity?.__typename ===
                ProviderActivityType.Assessment
                  ? selectedProviderActivity.assessment?.description
                  : undefined)
              }
            />
          ) : (
            <InterpretationSection assessment={detailedAssessment} />
          )}
          <ResponseOptionsSection questions={detailedAssessment?.questions} />
          <QuestionsSection
            questionFrame={detailedAssessment?.questionFrame}
            questions={detailedAssessment?.questions}
            isActivityForm={isActivityForm}
            showQuestionNumbers
          />
        </>
      )}
    </Slideover>
  );
};

export default ProviderActivitySlideover;
